import { useEffect, useState } from "react";
import { Loader2, UserCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ROLE_LABELS } from "@/lib/roles";

const ApproverPicker = ({ contractor, approvers, onSave }) => {
  const selected = contractor.approver_ids || [];
  const toggle = (id) => onSave(contractor, selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid={`assign-approvers-btn-${contractor.id}`} variant="outline" size="sm" className="gap-1.5">
          <UserCheck className="h-4 w-4" /> Choose
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Approvers for {contractor.name}</div>
        {approvers.map((a) => (
          <label key={a.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-secondary">
            <Checkbox data-testid={`approver-option-${contractor.id}-${a.id}`} checked={selected.includes(a.id)} onCheckedChange={() => toggle(a.id)} />
            <span className="flex-1 text-sm">{a.name}<span className="block text-xs text-muted-foreground">{a.email}</span></span>
            <span className="font-mono text-[10px] uppercase text-muted-foreground">{ROLE_LABELS[a.role]}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export const ApproverAssignments = () => {
  const [users, setUsers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [u, a] = await Promise.all([api.get("/admin/users"), api.get("/admin/approvers")]);
    setUsers(u.data.filter((x) => x.role === "employee"));
    setApprovers(a.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (contractor, ids) => {
    try {
      const { data } = await api.put(`/admin/users/${contractor.id}/approvers`, { approver_ids: ids });
      setUsers((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const nameOf = (id) => approvers.find((a) => a.id === id)?.name || "Removed user";

  if (loading) return <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <Card className="overflow-hidden" data-testid="approver-assignments">
      <div className="border-b border-border px-5 py-4 text-sm text-muted-foreground">
        Approvers only see and approve the contractors assigned to them. Admins always see everyone.
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Contractor</TableHead><TableHead>Approvers</TableHead><TableHead className="text-right">Assign</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.length === 0 && <TableRow><TableCell colSpan={3} className="py-10 text-center text-muted-foreground">No contractors yet. Add them in Team.</TableCell></TableRow>}
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`assignment-row-${u.id}`}>
                <TableCell><div className="font-medium">{u.name}</div><div className="font-mono text-xs text-muted-foreground">{u.email}</div></TableCell>
                <TableCell data-testid={`assignment-approvers-${u.id}`}>
                  {(u.approver_ids || []).length === 0 ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300"><AlertTriangle className="h-3.5 w-3.5" /> Unassigned - admins only</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">{u.approver_ids.map((id) => <Badge key={id} variant="secondary">{nameOf(id)}</Badge>)}</div>
                  )}
                </TableCell>
                <TableCell className="text-right"><ApproverPicker contractor={u} approvers={approvers} onSave={save} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
